import { Link } from 'react-router-dom';
import { formatCpmCount } from '../../../constants/centralProductMaster.constants';
import type { CpmStatsResponse } from '../../../types/centralProductMaster.types';
import SectionError from './SectionError';

interface ExciseReadinessPanelProps {
    readiness: CpmStatsResponse['exciseCategoryReadiness'];
    stateProductByState: Record<string, number>;
    infoOpen: boolean;
    onToggleInfo: () => void;
    error?: boolean;
    onRetry?: () => void;
    retrying?: boolean;
}

export default function ExciseReadinessPanel({
    readiness,
    stateProductByState,
    infoOpen,
    onToggleInfo,
    error = false,
    onRetry,
    retrying = false,
}: ExciseReadinessPanelProps) {
    const productsNoStateRow = readiness.states
        .filter((s) => s.isLive && !s.isCurated)
        .reduce((sum, s) => sum + (stateProductByState[s.name] ?? 0), 0);

    const liveStates = readiness.states.filter((s) => s.isLive);

    return (
        <div
            id="state-readiness"
            className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] flex flex-col"
            style={{ padding: 20, gap: 16 }}
        >
            <div className="flex items-baseline" style={{ gap: 10 }}>
                <h2 className="m-0 !text-[14px] !font-extrabold !text-[var(--cpm-text-primary)]">
                    Excise Category Readiness
                </h2>
                <button
                    type="button"
                    title="Why this matters"
                    onClick={onToggleInfo}
                    className="inline-flex items-center justify-center rounded-full bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)] border-0 cursor-pointer"
                    style={{ width: 20, height: 20, padding: 0 }}
                >
                    <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4" />
                        <path d="M12 8h.01" />
                    </svg>
                </button>
                {!error ? (
                    <span
                        className="ml-auto text-[var(--cpm-warning-text-med)]"
                        style={{ fontSize: 12, fontWeight: 800 }}
                    >
                        {readiness.uncuratedCount === 0
                            ? 'All live states ready'
                            : `${formatCpmCount(readiness.uncuratedCount)} of ${formatCpmCount(readiness.liveCount)} states not ready`}
                    </span>
                ) : null}
            </div>
            <span
                className="text-[var(--cpm-text-secondary)]"
                style={{ fontSize: 12.5, fontWeight: 700, marginTop: -8 }}
            >
                Excise category list curated, per state
            </span>
            {infoOpen ? (
                <span
                    className="bg-[var(--cpm-info-bg)] border border-[var(--cpm-info-border)] text-[var(--cpm-info-text)] rounded-[8px]"
                    style={{
                        marginTop: -4,
                        padding: '11px 13px',
                        fontSize: 12.5,
                        fontWeight: 600,
                        lineHeight: 1.45,
                    }}
                >
                    Until a state is marked live and its excise category list has been curated, the
                    linking pipeline will not create StateProduct rows for that state — it skips
                    those products silently. There is no review queue for the gap: the rows are
                    simply never created until the state is ready.
                </span>
            ) : null}

            {error ? (
                <SectionError
                    title="Couldn't load excise readiness"
                    body="State and category readiness did not return. Nothing has changed — retry to load this panel."
                    onRetry={onRetry}
                    retrying={retrying}
                />
            ) : (
                <>
                    <div
                        className="grid grid-cols-2"
                        style={{ gap: '10px 20px', padding: '2px 0 6px' }}
                    >
                        <div
                            className="flex items-center rounded-[8px] bg-[var(--cpm-surface-subtle)]"
                            style={{ gap: 10, padding: '11px 13px' }}
                        >
                            <span
                                className="uppercase text-[var(--cpm-text-secondary)]"
                                style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em' }}
                            >
                                States ready
                            </span>
                            <span
                                className="ml-auto tabular text-[var(--cpm-success-text)]"
                                style={{ fontSize: 19, fontWeight: 800 }}
                            >
                                {formatCpmCount(readiness.curatedCount)}
                            </span>
                        </div>
                        <div
                            className="flex items-center rounded-[8px] bg-[var(--cpm-warning-bg)] border border-[var(--cpm-warning-border)]"
                            style={{ gap: 10, padding: '11px 13px' }}
                        >
                            <span
                                className="uppercase text-[var(--cpm-warning-text)]"
                                style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em' }}
                            >
                                Products with no state row
                            </span>
                            <span
                                className="ml-auto tabular text-[var(--cpm-warning-text)]"
                                style={{ fontSize: 19, fontWeight: 800 }}
                            >
                                {formatCpmCount(productsNoStateRow)}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-col" style={{ gap: 8 }}>
                        <div
                            className="flex items-center uppercase text-[var(--cpm-text-tertiary)]"
                            style={{
                                gap: 10,
                                fontSize: 10,
                                fontWeight: 800,
                                letterSpacing: '0.08em',
                                padding: '0 2px',
                            }}
                        >
                            <span className="flex-1">State</span>
                            <span style={{ width: 96, textAlign: 'right' }}>StateProduct</span>
                            <span style={{ width: 120, textAlign: 'right' }}>
                                Excise categories
                            </span>
                        </div>

                        {liveStates.map((state) => {
                            const spCount = stateProductByState[state.name] ?? 0;
                            if (!state.isCurated) {
                                return (
                                    <Link
                                        key={state.name}
                                        to={`/central-product-master/excise-category?state=${encodeURIComponent(state.name)}`}
                                        className="rounded-[10px] bg-[var(--cpm-warning-bg)] border border-[var(--cpm-warning-border)] no-underline !text-inherit hover:border-[var(--cpm-warning-icon)] flex flex-col"
                                        style={{ gap: 8, padding: '13px 14px', margin: '2px 0' }}
                                    >
                                        <div className="flex items-center" style={{ gap: 10 }}>
                                            <svg
                                                width="15"
                                                height="15"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="var(--cpm-warning-icon)"
                                                strokeWidth="2"
                                                className="shrink-0"
                                            >
                                                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                                <path d="M12 9v4" />
                                                <path d="M12 17h.01" />
                                            </svg>
                                            <span
                                                className="flex-1 text-[var(--cpm-warning-text)]"
                                                style={{ fontSize: 13.5, fontWeight: 800 }}
                                            >
                                                {state.name}
                                            </span>
                                            <span
                                                className="tabular text-[var(--cpm-warning-text)]"
                                                style={{
                                                    width: 96,
                                                    textAlign: 'right',
                                                    fontSize: 14.5,
                                                    fontWeight: 800,
                                                }}
                                            >
                                                {formatCpmCount(spCount)}
                                            </span>
                                            <span
                                                className="flex justify-end"
                                                style={{ width: 120 }}
                                            >
                                                <span
                                                    className="inline-flex items-center rounded-full bg-white border border-[var(--cpm-warning-border)] text-[var(--cpm-warning-text)]"
                                                    style={{
                                                        padding: '4px 10px',
                                                        fontSize: 11.5,
                                                        fontWeight: 800,
                                                    }}
                                                >
                                                    Not set up
                                                </span>
                                            </span>
                                        </div>
                                        <div
                                            className="text-[var(--cpm-warning-text-med)]"
                                            style={{
                                                fontSize: 12.5,
                                                fontWeight: 600,
                                                lineHeight: 1.45,
                                            }}
                                        >
                                            Excise categories not set up — the pipeline skips this
                                            state. Curating this state&apos;s list unblocks
                                            StateProduct creation.
                                        </div>
                                        <span
                                            className="inline-flex items-center text-[var(--cpm-warning-text)]"
                                            style={{ gap: 6, fontSize: 12.5, fontWeight: 800 }}
                                        >
                                            Curate {state.name}&apos;s categories
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <path d="M5 12h14" />
                                                <path d="m12 5 7 7-7 7" />
                                            </svg>
                                        </span>
                                    </Link>
                                );
                            }
                            return (
                                <div
                                    key={state.name}
                                    className="flex items-center border-t border-[var(--cpm-border-default)]"
                                    style={{ gap: 10, padding: '11px 2px' }}
                                >
                                    <span
                                        className="flex-1"
                                        style={{ fontSize: 13.5, fontWeight: 700 }}
                                    >
                                        {state.name}
                                    </span>
                                    <span
                                        className="tabular"
                                        style={{
                                            width: 96,
                                            textAlign: 'right',
                                            fontSize: 14.5,
                                            fontWeight: 800,
                                        }}
                                    >
                                        {formatCpmCount(spCount)}
                                    </span>
                                    <span className="flex justify-end" style={{ width: 120 }}>
                                        <span
                                            className="inline-flex items-center rounded-full bg-[var(--cpm-success-bg)] border border-[var(--cpm-success-border)] text-[var(--cpm-success-text-light)]"
                                            style={{
                                                gap: 5,
                                                padding: '4px 10px',
                                                fontSize: 11.5,
                                                fontWeight: 800,
                                            }}
                                        >
                                            Curated
                                        </span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
